/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import classNames from "classnames";
import { Footer, IMG, Stack } from "components";
import { FC, ReactNode, useEffect } from "react";
import useEventRouting from "hooks/useEventRouting";
import ROUTES from "routes";
import { useUI } from "state/UIContext";
import useDevice from "utils/useDevice";

type Props = {
  desktopBG: string;
  mobileBG: string;
  desktopBGFallback: string;
  mobileBGFallback: string;
  children: ReactNode;
};

const ScreenPage: FC<Props> = ({
  desktopBG,
  mobileBG,
  desktopBGFallback,
  mobileBGFallback,
  children,
}) => {
  const { isCurrentPage } = useEventRouting();
  // Auth screens share the login artwork and must skip the gradient overlay.
  const isLoginPage =
    isCurrentPage(ROUTES.login) ||
    isCurrentPage(ROUTES.forgotPassword) ||
    isCurrentPage(ROUTES.createNewPassword);
  const { setLogoColor, colorScheme } = useUI();

  useEffect(() => {
    setLogoColor("white");
  }, []);
  const { isMobile } = useDevice();
  const isInIntroPage = isCurrentPage(ROUTES.welcome);
  return (
    <>
      <div
        className={classNames(
          "relative top-0 bottom-0",
          "flex flex-col md:flex-row min-h-full w-full md:w-auto",
          "md:mx-20 md:mt-[72px] md:mb-10 md:gap-x-4",
          "-bg--theme-colors-surface-neutral-level02 transition-colors duration-300",
          "-rounded-outer-container overflow-hidden",
        )}
      >
        <div className="md:flex-1 h-[50vh] md:h-[80vh] relative">
          <div
            className={classNames(
              "absolute top-0 start-0 w-full h-[100%] bg-gradient-to-b",
              {
                "from-[#ffffffdb]":
                  colorScheme === "light" && !isLoginPage && !isInIntroPage,
                "from-[#000000db]":
                  colorScheme === "dark" && !isLoginPage && !isInIntroPage,
              },
            )}
          ></div>
          {isMobile ? (
            <IMG
              src={mobileBG}
              fallbackSrc={mobileBGFallback}
              alt="Screen background img"
              className="h-full w-full object-cover"
            />
          ) : (
            <IMG
              src={desktopBG}
              fallbackSrc={desktopBGFallback}
              alt="Screen background img"
              className="h-full w-full object-cover"
            />
          )}
        </div>
        <Stack className="flex-1 justify-between items-center pb-4 md:relative">
          <main className="px-2 md:px-0 w-[320px] md:w-[380px] flex h-full items-center justify-center mx-auto z-[1] max-md:mb-16">
            <Stack component="section" className="p-4">
              {children}
            </Stack>
          </main>
          <div className="md:hidden">
            <Footer isScreenPage={true} />
          </div>
        </Stack>
      </div>
      <div className="max-md:hidden">
        <Footer isScreenPage={false} />
      </div>
    </>
  );
};
export default ScreenPage;
